import { useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function MultipleChoiceExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical data
  // These remain the source of truth for validation.
  // =========================================================
  const question =
    exercise.exercise_data?.question ||
    exercise.prompt ||
    "";

  const options =
    exercise.exercise_data?.options || [];

  const correctAnswer =
    exercise.exercise_data?.correct_answer;

  // =========================================================
  // Display-only ES / RU translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  const translatedQuestion =
    language === "es" || language === "ru"
      ? translatedData.question || null
      : null;

  const translatedOptions =
    language === "es" || language === "ru"
      ? translatedData.options || []
      : [];

  // =========================================================
  // Separate legacy mixed English + Chinese display text
  // =========================================================
  const splitEnglishChinese = (text = "") => {
    const firstChineseIndex = text.search(
      /[\u3400-\u4DBF\u4E00-\u9FFF]/
    );

    if (firstChineseIndex === -1) {
      return {
        english: text.trim(),
        chinese: null,
      };
    }

    return {
      english: text
        .slice(0, firstChineseIndex)
        .trim(),

      chinese: text
        .slice(firstChineseIndex)
        .trim(),
    };
  };

  const promptParts =
    splitEnglishChinese(exercise.prompt || "");

  const questionParts =
    splitEnglishChinese(question);

  const englishPrompt = promptParts.english;
  const chinesePrompt = promptParts.chinese;

  const englishQuestion = questionParts.english;
  const chineseQuestion = questionParts.chinese;

  // =========================================================
  // State
  // =========================================================
  const [selectedAnswer, setSelectedAnswer] =
    useState(null);

  const [submitted, setSubmitted] =
    useState(false);

  // =========================================================
  // Submit
  // selectedAnswer remains canonical option index.
  // =========================================================
  const handleSubmit = async () => {
    if (selectedAnswer === null) return;

    const isCorrect =
      selectedAnswer === correctAnswer;

    const success =
      await submitLearningExercise({
        exerciseId: exercise.id,
        answer: {
          selected_answer: selectedAnswer,
        },
        isCorrect,
      });

    if (!success) return;

    setSubmitted(true);
  };

  const handleTryAgain = () => {
    setSelectedAnswer(null);
    setSubmitted(false);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        Single Choice
      </div>

      {/* =====================================================
          Prompt
          EN: English only
          ZH: English + Chinese
          ES/RU: English + selected translation
      ====================================================== */}

      {englishPrompt &&
        englishPrompt !== englishQuestion && (
          <div style={styles.quizPath}>
            {englishPrompt}
          </div>
        )}

      {language === "zh" &&
        chinesePrompt &&
        chinesePrompt !== chineseQuestion && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {chinesePrompt}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedPrompt &&
        translatedPrompt !==
          translatedQuestion && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {translatedPrompt}
          </div>
        )}

      {/* =====================================================
          Question
      ====================================================== */}

      {englishQuestion && (
        <div
          style={{
            marginTop: "14px",
            fontSize: "17px",
            lineHeight: "1.6",
            fontWeight: "600",
          }}
        >
          {englishQuestion}
        </div>
      )}

      {language === "zh" &&
        chineseQuestion && (
          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
            }}
          >
            {chineseQuestion}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedQuestion && (
          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
            }}
          >
            {translatedQuestion}
          </div>
        )}

      {/* =====================================================
          Options
          Option index remains canonical.
      ====================================================== */}

      <div
        style={{
          display: "grid",
          gap: "10px",
          marginTop: "16px",
        }}
      >
        {options.map((option, index) => {
          const isSelected =
            selectedAnswer === index;

          const isCorrectOption =
            index === correctAnswer;

          const optionParts =
            splitEnglishChinese(option);

          const englishOption =
            optionParts.english;

          const chineseOption =
            optionParts.chinese;

          const translatedOption =
            translatedOptions[index]?.text || null;

          let optionStyle = {
            ...styles.optionButton,
          };

          if (!submitted && isSelected) {
            optionStyle = {
              ...optionStyle,
              ...styles.selectedOption,
            };
          }

          if (submitted && isCorrectOption) {
            optionStyle = {
              ...optionStyle,
              ...styles.correctOption,
            };
          }

          if (
            submitted &&
            isSelected &&
            !isCorrectOption
          ) {
            optionStyle = {
              ...optionStyle,
              backgroundColor: "#fef2f2",
              borderColor: "#dc2626",
            };
          }

          return (
            <button
              key={index}
              style={optionStyle}
              disabled={submitted}
              onClick={() =>
                setSelectedAnswer(index)
              }
            >
              <span style={styles.optionLetter}>
                {String.fromCharCode(65 + index)}.
              </span>

              <span>
                {/* Canonical English option */}
                <div>{englishOption}</div>

                {/* Chinese option */}
                {language === "zh" &&
                  chineseOption && (
                    <div
                      style={{
                        marginTop: "3px",
                        color: "#6b7280",
                        fontWeight: "400",
                        lineHeight: "1.5",
                      }}
                    >
                      {chineseOption}
                    </div>
                  )}

                {/* Spanish / Russian option */}
                {(language === "es" ||
                  language === "ru") &&
                  translatedOption && (
                    <div
                      style={{
                        marginTop: "3px",
                        color: "#6b7280",
                        fontWeight: "400",
                        lineHeight: "1.5",
                      }}
                    >
                      {translatedOption}
                    </div>
                  )}
              </span>
            </button>
          );
        })}
      </div>

      {/* =====================================================
          Submit / Result
      ====================================================== */}

      {!submitted ? (
        <button
          style={{
            ...styles.smallPrimaryButton,
            marginTop: "16px",
          }}
          disabled={
            submittingLearningExercise ||
            selectedAnswer === null
          }
          onClick={handleSubmit}
        >
          {submittingLearningExercise
            ? "Submitting..."
            : "Submit Answer"}
        </button>
      ) : (
        <div style={{ marginTop: "16px" }}>
          {selectedAnswer === correctAnswer ? (
            <div
              style={{
                color: "#16a34a",
                fontWeight: "bold",
              }}
            >
              Correct
            </div>
          ) : (
            <div
              style={{
                color: "#dc2626",
                fontWeight: "bold",
              }}
            >
              Incorrect · Correct answer:{" "}
              {String.fromCharCode(
                65 + correctAnswer
              )}
            </div>
          )}

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}